import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import MaterialButton from '@mui/material/Button';
import { ColorLens } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { FaFilter } from 'react-icons/fa';
import { ChooseFilters } from './wizard-steps/choose-filters';
import { ChooseStyle } from './wizard-steps/choose-style';
import { ChooseUser } from './wizard-steps/choose-user';
import { INFO } from '../../theme';

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-right: 16px;
  padding-bottom: 6px;
  padding-top: 6px;
  justify-content: center;
  height: 50px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const Button = styled.div`
  flex: 1;
  border-radius: 3px;
  padding: 12px;
  font-size: 14px;
  color: ${INFO};
  border: 1px solid ${INFO};
  font-weight: bold;
  margin: 0 6px 0 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;
  opacity: ${({ active }) => (active ? 1 : 0.5)};
  pointer-events: ${({ active }) => (active ? 'all' : 'none')};
  cursor: pointer;

  &:last-child {
    margin: 0;
  }
`;

const filterIconStyle = {
  color: `${INFO}`,
  cursor: 'pointer',
  paddingTop: '16px',
};

const colorPickerStyle = {
  color: `${INFO}`,
  cursor: 'pointer',
  paddingTop: '12px',
};

const STEPS = {
  FILTERS: {
    index: 0,
    component: ChooseFilters,
    nextStep: {
      label: 'Choose Colors',
      step: 'COLORS',
    },
  },
  COLORS: {
    index: 1,
    component: ChooseStyle,
    prevStep: {
      label: 'Choose Filters',
      step: 'FILTERS',
    },
  },
};

const mapState = ({
 newCustomLayers, reports, filters, shareLayer, customLayersConfig,
}) => {
  const newLayers = Object.values(newCustomLayers);

  if (!newLayers.length) return {};
  const customLayer = newLayers[0];
  return {
    multiParcelReport: reports.find(({ id }) => id === 'MULTI_PARCEL_REPORT'),
    customLayer,
    reports,
    hasMultiSources: get(filters, '[0].filters.length', false),
    shareLayer,
    savingEnabled: !customLayersConfig.disablelayerSaving,
  };
};

export const Wizard = ({ setOpenNav }) => {
  const [step, setStep] = useState('FILTERS');
  const [nameDialogOpen, setNameDialogOpen] = React.useState(false);
  const [label, setLabel] = React.useState('');
  const [openReport, setOpenReport] = React.useState(false);

  const {
    customLayer = {},
    multiParcelReport,
    hasMultiSources,
    shareLayer,
    savingEnabled,
  } = useSelector(
    mapState,
  );

  const dispatch = useDispatch();
  const stepDefinition = STEPS[step];

  const save = useCallback(({ openReport: openRep } = {}) => {
    if (customLayer.label.length > 260) {
      dispatch({
        type: 'OPEN_SNACKBAR',
        payload: {
          message: 'The layer name is too long. Layers names must be under 260 characters.',
        },
      });
      return;
    }

    dispatch({
      type: 'SAVE_NEW_LAYER',
      payload: {
        openReport: openRep,
      },
    });
    setOpenNav('LAYER_SELECTOR');
    dispatch({ type: 'DRAW_DISABLED' });
    dispatch({ type: 'CLEAR_DRAW_AREA' });
    dispatch({ type: 'CLEAR_SHARE_LAYER_USERS' });

    dispatch({
      type: 'ADD_LAYER',
      payload: {
        ...customLayer,
        isCustom: true,
        checked: true,
      },
    });
  }, [dispatch, customLayer, setOpenNav]);

  const saveAndOpenReport = useCallback(() => {
    save({ openReport: true });
  }, [save]);

  const handleClose = () => {
    setNameDialogOpen(false);
  };

  const handleSave = useCallback(() => {
    if (label.length > 260) {
      dispatch({
        type: 'OPEN_SNACKBAR',
        payload: {
          message: 'The layer name is too long. Layers names must be under 260 characters.',
        },
      });
      return;
    }

    dispatch({
      type: 'UPDATE_CUSTOM_LAYER_NAME',
      payload: {
        key: customLayer.key,
        label,
      },
    });
    setNameDialogOpen(false);

    if (openReport) {
      saveAndOpenReport();
      setOpenReport(false);
    } else {
      save();
      setOpenReport(false);
    }
  }, [
    customLayer.key,
    label,
    openReport,
    dispatch,
    setNameDialogOpen,
    save,
    saveAndOpenReport,
    setOpenReport,
  ]);

  if (!stepDefinition) return null;
  const StepComponent = stepDefinition.component;

  if (hasMultiSources && !customLayer.source) {
    return <StepComponent key={`${step}-multi-source`} setOpenNav={setOpenNav} />;
  }

  if (shareLayer) {
    return <ChooseUser />;
  }

  return [
    <StepComponent key={step} setOpenNav={setOpenNav} />,
    <ButtonContainer key={`${step}-button-container`}>
      <Spacer />
      <ButtonRow>
        {!stepDefinition.prevStep ? null : (
          <Tooltip
            title="Choose Filters"
            aria-label="choose-filters"
            key="choose-filters"
            placement="right"
            arrow
          >
            <span>
              <FaFilter
                style={filterIconStyle}
                onClick={() => setStep(stepDefinition.prevStep.step)}
              />
            </span>
          </Tooltip>
        )}
        {!stepDefinition.nextStep ? null : (
          <Tooltip
            title="Choose Colors"
            aria-label="choose-colors"
            key="choose-colors"
            placement="right"
            arrow
          >
            <ColorLens
              style={colorPickerStyle}
              onClick={() => {
                if (stepDefinition.nextStep.step === 'DONE') {
                  dispatch({ type: 'SAVE_NEW_LAYER' });
                  setOpenNav('LAYER_SELECTOR');
                } else {
                  setStep(stepDefinition.nextStep.step);
                }
              }}
            />
          </Tooltip>
        )}
      </ButtonRow>
      {savingEnabled && multiParcelReport &&
        !(multiParcelReport.excludeSources || []).includes(customLayer.source) ? (
          <ButtonRow>
            <Button
              active
              onClick={() => {
                if (!customLayer.label) {
                  setOpenReport(true);
                  return setNameDialogOpen(true);
                }
                return saveAndOpenReport();
              }}
            >
              Save and Open Report
            </Button>
          </ButtonRow>
        ) : null}
      <ButtonRow style={{ display: savingEnabled ? 'visible' : 'none' }}>
        <Button
          active
          onClick={() => {
            if (!customLayer.label) return setNameDialogOpen(true);
            return save();
          }}
        >
          Save
        </Button>
      </ButtonRow>
      <Dialog
        open={nameDialogOpen}
        onClose={() => setNameDialogOpen(false)}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Pick a Name</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Pick a name for your custom layer. Once you save you will find your
            custom layers in the layer menu.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Custom Layer Name"
            type="text"
            fullWidth
            onChange={(e) => setLabel(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <MaterialButton onClick={handleClose} color="primary">
            Cancel
          </MaterialButton>
          <MaterialButton onClick={handleSave} color="primary">
            Save
          </MaterialButton>
        </DialogActions>
      </Dialog>
    </ButtonContainer>,
  ];
};
